import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, X } from 'lucide-react';
import { userService } from '../_services';
import {
    buildItemListJson,
    createInitialScrollFlags,
    isScrollAtBottom,
    mergeLazyLoadItems,
    parseLazyLoadResponse
} from './filterLazyLoadUtils';

function CreateNewFilter({ text, closePopup, wholesaler_name }) {
    // Initialize user data and role
    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });

    const [roleType, roleName] = useMemo(() => {
        if (!userdata) return ['', ''];
        if (userdata.userrole === "Distributor" && userdata.wholesaler === "Avenel") {
            return ["distributor", userdata.paramsname];
        } else if (userdata.userrole === "Distributor" && userdata.wholesaler !== "Avenel") {
            return ["distributor", userdata.paramsname];
        } else {
            return [userdata.userrole, userdata.paramsname];
        }
    }, [userdata]);

    // Filter data arrays
    const [cityJson, setCityJson] = useState([]);
    const [stateJson, setStateJson] = useState([]);
    const [zipJson, setZipJson] = useState([]);
    const [drugJson, setDrugJson] = useState([]);
    const [pharmacyJson, setPharmacyJson] = useState([]);
    const [prescriberJson, setPrescriberJson] = useState([]);
    const [groupNameJson, setGroupNameJson] = useState([]);
    const [distributorJson, setDistributorJson] = useState([]);
    const [wholesalerJson, setWholesalerJson] = useState([]);
    const [pbmJson, setPbmJson] = useState([]);
    const [binJson, setBinJson] = useState([]);
    const [groupPrefixJson, setGroupPrefixJson] = useState([]);
    const [pharmacyChainJson, setPharmacyChainJson] = useState([]);

    // Search terms for each filter type
    const [searchTerms, setSearchTerms] = useState({
        PhCityInput: '',
        PhStateInput: '',
        PhZipInput: '',
        PBMInput: '',
        DrugNameInput: '',
        PharmacyNameInput: '',
        PrescriberInput: '',
        GroupNumberInput: '',
        DistributorNameInput: '',
        WholesalerNameInput: '',
        PhBinInput: '',
        PhGPrefix: '',
        PharmacyChainInput: ''
    });

    // Selected filters - array of {name, value} objects
    const [selectedFilters, setSelectedFilters] = useState([]);

    // UI state
    const [loading, setLoading] = useState({
        CITY: false,
        ZIP: false,
        DRUG: false,
        PHARMACYNAME: false,
        PRESCRIBERNAME: false,
        GROUPNUMBER: false,
        DISTRIBUTOR: false,
        WHOLESALER: false
    });
    const [prescribeStatus, setPrescribeStatus] = useState(false);
    const [showSaveSection, setShowSaveSection] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);

    // Track if there's more data to load for each filter type
    const [hasMoreData, setHasMoreData] = useState({
        CITY: true,
        ZIP: true,
        DRUG: true,
        PHARMACYNAME: true,
        PRESCRIBERNAME: true,
        GROUPNUMBER: true,
        DISTRIBUTOR: true,
        WHOLESALER: true
    });

    // Filter visibility state (for static filters)
    const [visibleItems, setVisibleItems] = useState({});

    const filterDataRef = useRef({});
    const isScrollLoadingRef = useRef({});
    const canLoadMoreRef = useRef(createInitialScrollFlags());
    const hasReachedEndRef = useRef({});

    useEffect(() => {
        filterDataRef.current = {
            CITY: cityJson,
            ZIP: zipJson,
            DRUG: drugJson,
            PHARMACYNAME: pharmacyJson,
            PRESCRIBERNAME: prescriberJson,
            GROUPNUMBER: groupNameJson,
            DISTRIBUTOR: distributorJson,
            WHOLESALER: wholesalerJson
        };
    }, [cityJson, zipJson, drugJson, pharmacyJson, prescriberJson, groupNameJson, distributorJson, wholesalerJson]);

    // Check if data is loaded
    const isDataLoaded = useMemo(() => {
        return cityJson.length > 0 || stateJson.length > 0 || zipJson.length > 0 || pbmJson.length > 0;
    }, [cityJson, stateJson, zipJson, pbmJson]);

    // Handle response
    const handleResponse = useCallback(async (response) => {
        try {
            const text = await response.text();
            const data = text && JSON.parse(text);
            return data;
        } catch {
            return [];
        }
    }, []);

    // Fetch prescriber info
    const fetchPrescriberInfo = useCallback(async () => {
        if (!userdata?.wholesaler_Id) return;
        try {
            const whid = userdata.wholesaler_Id.toString();
            const apidata = await userService.WhPrescribeInfo(whid);
            if (apidata?.length > 0) {
                setPrescribeStatus(apidata[0]._IsPrescribeData);
            }
        } catch (error) {
        }
    }, [userdata]);

    // Get filter raw data
    const getFilterRawDataDetail = useCallback(async (username, usertype, whname, distributor_UserId) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, usertype, whname, distributor_UserId })
            };
            const response = await fetch('/api/Filter/OnLoadFilterData', requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.[0]) {
                setCityJson(apidata[0]["fcity"] || []);
                setStateJson(apidata[0]["fstate"] || []);
                setZipJson(apidata[0]["fzip"] || []);
                setDrugJson(apidata[0]["fdrug"] || []);
                setPharmacyJson(apidata[0]["fpharmacy"] || []);
                setPrescriberJson(apidata[0]["fprescriber"] || []);
                setGroupNameJson(apidata[0]["groupNumber"] || []);
                setDistributorJson(apidata[0]["distributor"] || []);
                setWholesalerJson(apidata[0]["wholesaler"] || []);
                setPbmJson(apidata[0]["fpbm"] || []);
                setBinJson(apidata[0]["bin"] || []);
                setGroupPrefixJson(apidata[0]["gnPrefix"] || []);
                setPharmacyChainJson(apidata[0]["pharmacyChain"] || []);
            }
        } catch (error) {
        }
    }, [handleResponse]);

    // Get lazy loading data for search
    const applyLazyLoadResult = useCallback((stype, currentItems, apidata) => {
        const { newArr, addedCount } = mergeLazyLoadItems(currentItems, apidata);

        if (stype === 'CITY') setCityJson(newArr);
        else if (stype === 'ZIP') setZipJson(newArr);
        else if (stype === 'DRUG') setDrugJson(newArr);
        else if (stype === 'PHARMACYNAME') setPharmacyJson(newArr);
        else if (stype === 'PRESCRIBERNAME') setPrescriberJson(newArr);
        else if (stype === 'GROUPNUMBER') setGroupNameJson(newArr);
        else if (stype === 'DISTRIBUTOR') setDistributorJson(newArr);
        else if (stype === 'WHOLESALER') setWholesalerJson(newArr);

        return addedCount;
    }, []);

    const getNewItemSearchLazyLoading = useCallback(async (stext, stype, roletype, rolename, whname, itemlist, filterType, currentItems = []) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': itemlist
                },
                body: JSON.stringify({ stext, stype, roletype, rolename, whname })
            };
            const response = await fetch('/api/Filter/LazyLoadingData', requestOptions);
            const apidata = await parseLazyLoadResponse(response);
            const addedCount = applyLazyLoadResult(stype, currentItems, apidata);

            if (filterType === 'scroll') {
                if (addedCount === 0) {
                    hasReachedEndRef.current[stype] = true;
                    setHasMoreData(prev => ({ ...prev, [stype]: false }));
                    canLoadMoreRef.current[stype] = false;
                } else {
                    canLoadMoreRef.current[stype] = true;
                }
            }
        } catch (error) {
            if (filterType === 'scroll') {
                canLoadMoreRef.current[stype] = true;
            }
        }
    }, [applyLazyLoadResult]);

    // Handle checkbox change
    const handleCheckbox = useCallback((name, value, checked) => {
        if (checked) {
            setSelectedFilters(prev => {
                const exists = prev.find(f => f.name === name && f.value === value);
                if (!exists) {
                    return [...prev, { name, value }];
                }
                return prev;
            });
            setErrorMsg('');
        } else {
            setSelectedFilters(prev => prev.filter(f => !(f.name === name && f.value === value)));
        }
    }, []);

    // Handle search input
    const handleSearchChange = useCallback((fieldName, value, scrollId) => {
        setSearchTerms(prev => ({ ...prev, [fieldName]: value }));

        // For static filters (State, PBM, Bin, Pharmacy Chain), just filter visibility client-side
        if (fieldName === 'PhStateInput' || fieldName === 'PBMInput' || fieldName === 'PhBinInput' || fieldName === 'PhGPrefix' || fieldName === 'PharmacyChainInput') {
            return;
        }

        // For dynamic filters, trigger lazy loading if >= 3 characters
        if (value.length >= 3 && scrollId) {
            setLoading(prev => ({ ...prev, [scrollId]: true }));
            const currentItems = filterDataRef.current[scrollId] || [];
            const itemList = buildItemListJson(currentItems);

            getNewItemSearchLazyLoading(
                value,
                scrollId,
                userdata.userrole.toString(),
                userdata.distributor_UserId?.toString() || '',
                userdata.wholesaler_Id?.toString() || '',
                itemList,
                'TypeSearch',
                currentItems
            ).finally(() => setLoading(prev => ({ ...prev, [scrollId]: false })));
        }
    }, [getNewItemSearchLazyLoading, userdata]);

    // Handle scroll for lazy loading
    const handleScroll = useCallback((e, scrollType) => {
        if (!scrollType) return;

        const element = e.target;
        const isAtBottom = isScrollAtBottom(element);

        if (hasReachedEndRef.current[scrollType]) return;

        if (!isAtBottom) {
            canLoadMoreRef.current[scrollType] = true;
            return;
        }

        if (isScrollLoadingRef.current[scrollType] || canLoadMoreRef.current[scrollType] === false) {
            return;
        }

        canLoadMoreRef.current[scrollType] = false;
        isScrollLoadingRef.current[scrollType] = true;
        setLoading(prev => ({ ...prev, [scrollType]: true }));

        const currentItems = filterDataRef.current[scrollType] || [];
        const itemList = buildItemListJson(currentItems);

        getNewItemSearchLazyLoading(
            '',
            scrollType,
            userdata.userrole.toString(),
            userdata.distributor_UserId?.toString() || '',
            userdata.wholesaler_Id?.toString() || '',
            itemList,
            'scroll',
            currentItems
        ).finally(() => {
            isScrollLoadingRef.current[scrollType] = false;
            setLoading(prev => ({ ...prev, [scrollType]: false }));
        });
    }, [getNewItemSearchLazyLoading, userdata]);

    // Build selected tag JSON
    const buildSelectedTagJson = useCallback(() => {
        if (selectedFilters.length === 0) return '';

        const grouped = {};
        selectedFilters.forEach(filter => {
            if (!grouped[filter.name]) {
                grouped[filter.name] = [];
            }
            grouped[filter.name].push(filter.value);
        });

        let jsonData = '';
        Object.keys(grouped).forEach(nodeName => {
            const values = grouped[nodeName].map(v => `"${v}"`).join(',');
            if (jsonData === '') {
                jsonData = `"${nodeName}": [${values}]`;
            } else {
                jsonData += `,"${nodeName}": [${values}]`;
            }
        });

        return `{"dataset": {${jsonData}}}`;
    }, [selectedFilters]);

    // Handle save filter
    const handleSaveFilter = useCallback(async () => {
        setErrorMsg('');
        const filterNameVal = filterName.trim();

        if (!filterNameVal) {
            setErrorMsg('Please enter a filter name');
            return;
        }

        if (userService.ValidateSpace(filterNameVal)) {
            setErrorMsg('Space not acceptable');
            return;
        }

        const createdTagJson = buildSelectedTagJson();
        if (!createdTagJson || createdTagJson === '{"dataset": {}}') {
            setErrorMsg('Please make a selection above');
            return;
        }

        setSaveLoading(true);
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Browser-Url': window.location.href },
                body: JSON.stringify({
                    FilterName: filterNameVal,
                    FilterJson: createdTagJson,
                    DashboardId: userdata?.dList?.[0]?.dashboardId
                })
            };
            const response = await fetch('/api/Filter/SaveUserAdFilter', requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                if (apidata[0].status_code === "-1") {
                    setErrorMsg(apidata[0].status_message);
                    setTimeout(() => {
                        setShowSaveSection(false);
                        setSaveLoading(false);
                        closePopup();
                    }, 5000);
                } else {
                    setShowSaveSection(false);
                    setSaveLoading(false);
                    closePopup();
                }
            } else {
                setSaveLoading(false);
            }
        } catch (error) {
            setSaveLoading(false);
        }
    }, [filterName, buildSelectedTagJson, userdata, handleResponse, closePopup]);

    // Handle apply filter
    const handleApplyFilter = useCallback(() => {
        if (selectedFilters.length === 0) {
            setErrorMsg('Please make a selection above');
            return;
        }

        const filterJson = buildSelectedTagJson();
        localStorage.setItem('Advance_Filter', JSON.stringify(JSON.parse(filterJson).dataset));
        window.location.href = window.location.origin + '/dashboard';
    }, [selectedFilters, buildSelectedTagJson]);

    // Handle reset filter
    const handleResetFilter = useCallback(() => {
        setSelectedFilters([]);
        setSearchTerms({
            PhCityInput: '',
            PhStateInput: '',
            PhZipInput: '',
            PBMInput: '',
            DrugNameInput: '',
            PharmacyNameInput: '',
            PrescriberInput: '',
            GroupNumberInput: '',
            DistributorNameInput: '',
            WholesalerNameInput: '',
            PhBinInput: '',
            PhGPrefix: '',
            PharmacyChainInput: ''
        });
        setErrorMsg('');
    }, []);

    // Remove selected filter
    const removeSelectedFilter = useCallback((name, value) => {
        setSelectedFilters(prev => prev.filter(f => !(f.name === name && f.value === value)));
    }, []);

    // Filter items by search term
    const getFilteredItems = useCallback((items, searchTerm) => {
        if (!searchTerm) return items;
        const term = searchTerm.toLowerCase();
        return items.filter(item => item.text?.toLowerCase().includes(term));
    }, []);

    // Initialize on mount
    useEffect(() => {
        if (!userdata) return;

        fetchPrescriberInfo();

        if (userdata.userrole === "Distributor" && userdata.wholesaler === "Avenel") {
            getFilterRawDataDetail(roleName, roleType, userdata.wholesaler_Id, userdata.distributor_UserId);
        } else if (userdata.userrole === "Distributor" && userdata.wholesaler !== "Avenel") {
            getFilterRawDataDetail(roleName, roleType, userdata.wholesaler_Id, userdata.distributor_UserId);
        } else {
            getFilterRawDataDetail(roleName, roleType, userdata.wholesaler_Id, -1);
        }
    }, [userdata, roleName, roleType, fetchPrescriberInfo, getFilterRawDataDetail]);

    // Render filter section
    const renderFilterSection = (title, filterName, items, searchField, scrollType, searchPlaceholder = 'Search') => {
        const searchTerm = searchTerms[searchField] || '';
        const filteredItems = getFilteredItems(items, searchTerm);
        const isChecked = (value) => selectedFilters.some(f => f.name === filterName && f.value === value);

        return (
            <div className="repeat-section" id={`${filterName}Input0`}>
                <h3 className="mb-1">{title}</h3>
                <div className="searchSection relative mb-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                        id={`${filterName}Input1`}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(searchField, e.target.value, scrollType)}
                        className="searchFilter pl-10"
                        autoComplete="off"
                    />
                </div>
                <ScrollArea
                    className="scroll-section h-48"
                    onScroll={(e) => scrollType && handleScroll(e, scrollType)}
                >
                    {filteredItems.length > 0 ? (
                        <div className="checkbox-section py-2 space-y-2">
                            {filteredItems.map((item, index) => (
                                <div className="same-checkbox flex items-center gap-2" key={`${filterName}${index}`}>
                                    <Checkbox
                                        id={`${filterName}${index}`}
                                        checked={isChecked(item.text)}
                                        onCheckedChange={(checked) => handleCheckbox(filterName, item.text, checked)}
                                    />
                                    <Label htmlFor={`${filterName}${index}`} className="cursor-pointer text-xs">
                                        {item.text}
                                    </Label>
                                </div>
                            ))}
                            {scrollType && loading[scrollType] && (
                                <div className="flex justify-center py-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                </div>
                            )}
                        </div>
                    ) : null}
                </ScrollArea>
            </div>
        );
    };

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup();
        }
    }, [closePopup]);

    if (!isDataLoaded) {
        return (
            <Dialog open={true} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>{text}</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center justify-center p-8 min-h-[120px] flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="ml-3">Please wait...</p>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    const shouldShowPrescriber = (prescribeStatus === true && (userdata?.userrole === 'Wholesaler' || userdata?.userrole === 'Distributor')) ||
        (userdata?.userrole === "Admin" || userdata?.userrole === "SubAdmin");

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-6xl h-[90vh] flex flex-col p-0">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="filter-Content flex-1 overflow-y-auto px-0 space-y-4">
                    <div className="filter-scroll grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userdata?.username !== 'ID.me' && userdata?.email !== 'alex.von.reyn@id.me' && (
                            <>
                                {renderFilterSection('Pharmacy City', 'PhCity', cityJson, 'PhCityInput', 'CITY')}
                                {renderFilterSection('Pharmacy State', 'PhState', stateJson, 'PhStateInput', null)}
                                {renderFilterSection('Pharmacy Zip', 'PhZip', zipJson, 'PhZipInput', 'ZIP')}
                            </>
                        )}

                        {renderFilterSection('PCN', 'PBM', pbmJson, 'PBMInput', null)}
                        {renderFilterSection('Drug Name', 'DrugName', drugJson, 'DrugNameInput', 'DRUG')}
                        {renderFilterSection('Pharmacy Name', 'PharmacyName', pharmacyJson, 'PharmacyNameInput', 'PHARMACYNAME')}

                        {shouldShowPrescriber && (
                            renderFilterSection('Prescriber Name', 'Prescriber', prescriberJson, 'PrescriberInput', 'PRESCRIBERNAME')
                        )}

                        {renderFilterSection('Group Number', 'GroupNumber', groupNameJson, 'GroupNumberInput', 'GROUPNUMBER')}

                        {userdata?.userrole !== "Distributor" && distributorJson.length > 0 && (
                            renderFilterSection('Distributor Name', 'DistributorName', distributorJson, 'DistributorNameInput', 'DISTRIBUTOR')
                        )}

                        {(userdata?.userrole === "Admin" || userdata?.userrole === "SubAdmin") && (
                            renderFilterSection('Wholesaler Name', 'WholesalerName', wholesalerJson, 'WholesalerNameInput', 'WHOLESALER')
                        )}

                        {renderFilterSection('BIN', 'PhBin', binJson, 'PhBinInput', null)}

                        {(userdata?.userrole === "Wholesaler" || userdata?.userrole === "Admin") && pharmacyChainJson.length > 0 && (
                            renderFilterSection('Pharmacy Chain', 'Pharmacy Chain', pharmacyChainJson, 'PharmacyChainInput', null)
                        )}

                        {(userdata?.userrole === "Wholesaler" || userdata?.userrole === "Admin") && (
                            renderFilterSection('Group Prefix', 'Group Prefix', groupPrefixJson, 'PhGPrefix', null)
                        )}
                    </div>

                </div>

                <DialogFooter className="grid grid-cols-1 gap-2 px-6 pb-4 border-t bg-card pt-3">


                    {/* Error message display for Apply Filter validation */}
                    {errorMsg && !showSaveSection && (
                        <div className="p-0">
                            <div className="text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-md p-3">
                                {errorMsg}
                            </div>
                        </div>
                    )}
                    <div className="selected-filters">
                        <Label>Selected Filters</Label>
                        <div className="flex flex-nowrap gap-1 mt-2 overflow-x-auto">
                            {selectedFilters.map((filter, index) => (
                                <Badge
                                    key={index}
                                    variant="outline"
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-normal leading-none whitespace-nowrap bg-primary/10 text-primary border-primary"
                                >
                                    <span className="city-name">
                                        {filter.value.length > 10 ? filter.value.substring(0, 7) + '...' : filter.value}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeSelectedFilter(filter.name, filter.value)}
                                        className="h-4 w-4 p-0 hover:bg-transparent hover:text-current [&_svg]:size-2.5"
                                    >
                                        <X />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                    </div>

                    {/* Button section - Save Filter on left, Reset/Apply on right */}
                    <div className="flex justify-between items-center gap-2">
                        {/* Left: Save Filter button with popover dropdown */}
                        <Popover open={showSaveSection} onOpenChange={setShowSaveSection}>
                            <PopoverTrigger asChild>
                                <Button
                                    className="bg-primary text-primary-foreground shadow-sm"
                                >
                                    Save Filter
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-80 p-4"
                                side="top"
                                align="start"
                                sideOffset={5}
                            >
                                <div className="space-y-3">
                                    <h3 className="text-md font-medium">Save Filter</h3>
                                    <div className="space-y-2">
                                        <Label htmlFor="FilterNameTextBottom">Name</Label>
                                        <Input
                                            id="FilterNameTextBottom"
                                            type="text"
                                            placeholder="Filter set 1"
                                            value={filterName}
                                            onChange={(e) => {
                                                setFilterName(e.target.value);
                                                setErrorMsg('');
                                            }}
                                            className={errorMsg ? 'border-destructive' : ''}
                                        />
                                    </div>
                                    {errorMsg && (
                                        <div className="text-destructive text-sm">
                                            {errorMsg}
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleSaveFilter}
                                            disabled={saveLoading}
                                            className="bg-primary text-primary-foreground flex-1"
                                        >
                                            {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowSaveSection(false);
                                                setErrorMsg('');
                                            }}
                                            className="flex-1"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>

                        {/* Right: Reset, Apply Filter, and Cancel buttons */}
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                className="h-auto py-1 px-3 gap-1.5 whitespace-nowrap leading-[1.15] bg-foreground text-card border border-foreground hover:bg-foreground hover:text-card hover:opacity-85"
                                onClick={handleResetFilter}
                            >
                                Reset
                            </Button>
                            <Button
                                onClick={handleApplyFilter}
                                className="bg-primary text-primary-foreground shadow-sm"
                            >
                                Apply Filter
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={closePopup}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateNewFilter;
